import { currencyFractionDigits } from '../../../shared/utils/commercial-money';

import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService, SelectItem } from 'primeng/api';
import { catchError, forkJoin, of } from 'rxjs';
import jsPDF from 'jspdf';

// PrimeNG Modules
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { FieldsetModule } from 'primeng/fieldset';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { TextareaModule } from 'primeng/textarea';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { PanelModule } from "primeng/panel";
import { OrdersService } from '../../order.service';
import { SalesOrder } from '../sales-order.model';
import { SalesOrderLine } from '../sales-order-line.model';
import { FlexFieldValue } from 'src/app/shared/flex-fields/flex-field.model';
import { FlexFieldViewComponent } from 'src/app/shared/flex-fields/flex-field-view/flex-field-view.component';
import { ProductType } from '../../models/product-type.model';
import { TagModule } from "primeng/tag";
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from 'src/app/shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { TooltipModule } from 'primeng/tooltip';
import { TabsModule } from 'primeng/tabs';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { EditorModule } from 'primeng/editor';
import { FileUploadModule } from 'primeng/fileupload';
import { ProgressBarModule } from 'primeng/progressbar';
import { OrderActivity } from '../../order-workbench.model';
import { getOrderStatusMetadata } from '../../order-status-metadata';
import { SalesOrderActionMetadata, SalesOrderLifecycleActionRequest } from '../sales-order.model';
import {
    FulfillmentReadinessResponse, ReleaseStatusResponse,
    CreateReleaseRequest, CreateReturnReleaseRequest, ReleaseLineRequest,
    SalesOrderQuantityCorrectionPreview, SalesOrderQuantityCorrectionRequest
} from '../sales-order-fulfillment.model';
import {
    InvoiceReadinessResponse
} from '../sales-order-invoice-readiness.model';
import { FirebaseStorageService } from 'src/app/services/firebase-storage.service';
import { SalesOrderAttachment } from '../sales-order-attachment.model';
import { PermissionService } from 'src/app/security/permission.service';
import { app_permissions } from 'src/app/security/app-permissions';
import { EnterpriseFeatureService } from 'src/app/services/enterprise-features.service';
import { LazyLocationPickerDirective } from 'src/app/shared/location-picker/lazy-location-picker.directive';
import { LogisticsProjectsService } from 'src/app/logistics-projects/logistics-projects.service';
import { LogisticsProjectSummary } from 'src/app/logistics-projects/models/logistics-project.model';

interface HeroLifecycleAction {
	code: string;
	label: string;
	icon: string;
	severity: 'secondary' | 'success' | 'danger' | 'warn' | 'info' | 'help';
	outlined?: boolean;
}

interface DeliveryReleaseDraftLine {
	lineId: number;
	lineNumber?: string;
	productName?: string;
	quantity: number;
	maximum: number;
	warehouseId?: number;
}

@Component({
	selector: 'app-view-sales-order',
	templateUrl: './view-sales-order.component.html',
	styleUrls: ['./view-sales-order.component.css', '../../order-attachment-delete-dialog.css'],
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		RouterModule,
		BreadcrumbModule,
		FieldsetModule,
		InputGroupModule,
		InputGroupAddonModule,
		InputTextModule,
		InputNumberModule,
		SelectModule,
		DatePickerModule,
		TextareaModule,
		TableModule,
		ButtonModule,
		IftaLabelModule,
		PanelModule,
		FlexFieldViewComponent,
		TagModule,
		PageShellComponent,
		PageDetailHeroComponent,
		PageDataPanelComponent,
		PageMetricStripComponent,
		TooltipModule,
		TabsModule,
		DialogModule,
		ToastModule,
		EditorModule,
		FileUploadModule,
		ProgressBarModule,
		LazyLocationPickerDirective
	]
})
export class ViewSalesOrderComponent implements OnInit {
	private readonly tabKeys = ['overview', 'lines', 'control', 'audit', 'files', 'workflow', 'fulfillment', 'invoice'] as const;
	readonly heroLifecycleActions: HeroLifecycleAction[] = [
		{ code: 'SUBMIT', label: 'Submit', icon: 'pi pi-send', severity: 'secondary', outlined: true },
		{ code: 'APPROVE', label: 'Approve', icon: 'pi pi-check-circle', severity: 'success', outlined: true },
		{ code: 'REJECT', label: 'Reject', icon: 'pi pi-times-circle', severity: 'danger', outlined: true },
		{ code: 'CONFIRM', label: 'Confirm', icon: 'pi pi-verified', severity: 'success' },
		{ code: 'HOLD', label: 'Hold', icon: 'pi pi-lock', severity: 'warn', outlined: true },
		{ code: 'RELEASE_HOLD', label: 'Release', icon: 'pi pi-lock-open', severity: 'info', outlined: true },
		{ code: 'CLOSE', label: 'Close', icon: 'pi pi-check-square', severity: 'secondary', outlined: true },
		{ code: 'REOPEN', label: 'Reopen', icon: 'pi pi-refresh', severity: 'secondary', outlined: true },
		{ code: 'CANCEL', label: 'Cancel', icon: 'pi pi-ban', severity: 'danger', outlined: true }
	];

	breadcrumbItems: MenuItem[] = [];
	homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

	salesOrder: SalesOrder | undefined;
	actionMetadata: SalesOrderActionMetadata | undefined;
	activity: OrderActivity[] = [];
	currencyCode: string = 'SAR';
	activeTabIndex = 0;
	actionLoading = false;
	actionDialogVisible = false;
	pendingAction = '';
	actionReason = '';
	actionReasonCode = '';
	creditOverrideId: number | undefined;
	notesDraft = '';
	notesEditing = false;
	notesSaving = false;
	attachmentUploadProgress = 0;
	attachmentUploading = false;
	attachmentToDelete?: SalesOrderAttachment;
	attachmentDeleteDialogVisible = false;
	attachmentDeleteReason = '';
	attachmentDeleteError = '';
	attachmentDeleting = false;

	// Phase 5: Fulfillment & Invoice Readiness
	fulfillmentReadiness: FulfillmentReadinessResponse | undefined;
	fulfillmentReleases: ReleaseStatusResponse | undefined;
	invoiceReadiness: InvoiceReadinessResponse | undefined;
	syncLoading = false;
	draftLoading = false;
	deliveryReleaseDialogVisible = false;
	returnReleaseDialogVisible = false;
	lineSidePanelVisible = false;
	selectedLine: SalesOrderLine | undefined;
	deliveryReleaseLines: DeliveryReleaseDraftLine[] = [];
	deliveryReleaseRequestId = '';
	deliveryDestinationLocationId?: number;
	deliveryWindowStart?: Date;
	deliveryWindowEnd?: Date;
	projectNames: Record<number, string> = {};
	correctionVisible = false;
	correctionLoading = false;
	correctionPreview?: SalesOrderQuantityCorrectionPreview;
	correctionQuantity?: number;
	correctionReason = '';
	correctionGroups: SalesOrderQuantityCorrectionRequest['groups'] = [];
	cancelReleaseVisible = false;
	cancelReleaseRootId?: number;
	cancelReleaseReason = '';

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private sessionStorageService: SessionStorageService,
		private ordersService: OrdersService,
		private messageService: MessageService,
		private firebaseStorageService: FirebaseStorageService,
		private permissionService: PermissionService,
		private enterpriseFeatureService: EnterpriseFeatureService,
		private logisticsProjectsService: LogisticsProjectsService
	) { }

	ngOnInit(): void {
		this.breadcrumbItems = [
			{ label: 'Orders', routerLink: '/order/sales-order' },
			{ label: 'View Sales Order' }
		];
		if (!this.enterpriseFeatureService.isLoaded()) {
			this.enterpriseFeatureService.getAllSubscriptions().subscribe();
		}

		const owningCompany = this.sessionStorageService.getCompany();
		const currentCountry = this.sessionStorageService.getCountryList()?.find(country => country.id === owningCompany?.countryId);
		if (currentCountry) {
			this.currencyCode = currentCountry.currencyCode;
		}

		this.route.params.subscribe(params => {
			const id = +params['id'];
			if (id) {
				forkJoin({
					order: this.ordersService.getSalesOrderById(id),
					activity: this.ordersService.getSalesOrderActivity(id),
					actions: this.ordersService.getSalesOrderActions(id)
				}).subscribe(({ order, activity, actions }) => {
					this.salesOrder = order;
					this.activity = activity ?? [];
					this.actionMetadata = actions;
					this.currencyCode = order.currencyCode || this.currencyCode;
					this.notesDraft = order.notes || '';
					this.loadProjectNames(order);
					this.loadFulfillmentData();
					this.normalizeServiceOrderActiveTab();
					const correctionLineId = Number(this.route.snapshot.queryParamMap.get('correctionLineId'));
					const correctionLine = order.salesOrderLines?.find(line => line.lineID === correctionLineId);
					if (correctionLine && this.canCorrectQuantity(correctionLine)) {
						this.openQuantityCorrection(correctionLine);
					}
				});
			}
		});
		this.route.queryParamMap.subscribe(params => {
			this.activeTabIndex = this.resolveTabIndex(params.get('tab'));
		});
	}

	load(): void {
		const id = this.salesOrder?.orderID;
		if (!id) {
			return;
		}
		forkJoin({
			order: this.ordersService.getSalesOrderById(id),
			activity: this.ordersService.getSalesOrderActivity(id),
			actions: this.ordersService.getSalesOrderActions(id)
		}).subscribe(({ order, activity, actions }) => {
			this.salesOrder = order;
			this.activity = activity ?? [];
			this.actionMetadata = actions;
			this.currencyCode = order.currencyCode || this.currencyCode;
			this.notesDraft = order.notes || '';
			this.loadProjectNames(order);
			this.loadFulfillmentData();
			this.normalizeServiceOrderActiveTab();
		});
	}

	loadFulfillmentData(): void {
		const id = this.salesOrder?.orderID;
		if (!id) return;
		this.ordersService.getTransportReleases(id).subscribe(data => {
			this.fulfillmentReleases = data;
		});
		this.ordersService.getInvoiceReadiness(id).subscribe(data => {
			this.invoiceReadiness = data;
		});
	}

	hasHeaderFlexFields(): boolean {
		return (this.salesOrder?.flexFieldValues?.length ?? 0) > 0;
	}

	getHeaderFlexFields(): FlexFieldValue[] {
		return this.salesOrder?.flexFieldValues ?? [];
	}

	getLineFlexFields(line: SalesOrderLine | undefined): FlexFieldValue[] {
		return line?.flexFieldValues ?? [];
	}

	isInvoiceGenerated(): boolean {
		if (this.salesOrder?.invoiceGenerated) {
			return true;
		}
		const state = (this.salesOrder?.state || '').toUpperCase();
		if (state === 'INVOICED') {
			return true;
		}
		const billingStatus = (this.salesOrder?.billingStatus || '').toUpperCase();
		if (['INVOICED', 'PARTIALLY_INVOICED', 'INVOICE_DRAFTED', 'BILLED'].includes(billingStatus)) {
			return true;
		}
		return false;
	}

	isEditLocked(): boolean {
		return this.isInvoiceGenerated();
	}

	editLockedTooltip(): string {
		if (this.isInvoiceGenerated()) {
			return 'Order editing is locked: Customer invoice has already been generated.';
		}
		return 'Edit sales order lines and details';
	}

	editOrder() {
		if (this.salesOrder && this.salesOrder.orderID) {
			if (this.isEditLocked()) {
				this.messageService.add({
					severity: 'warn',
					summary: 'Editing Locked',
					detail: this.editLockedTooltip()
				});
				return;
			}
			this.router.navigate(['/order/sales-order/edit', this.salesOrder.orderID]);
		}
	}

	hasLogisticsProject(): boolean {
		return !!this.salesOrder?.logisticsProjectId;
	}

	viewLogisticsProject(): void {
		if (this.salesOrder?.logisticsProjectId) {
			this.router.navigate(['/logistics-projects', this.salesOrder.logisticsProjectId]);
		}
	}

	logisticsProjectName(projectId?: number): string {
		if (!projectId) {
			return '-';
		}
		if (projectId === this.salesOrder?.logisticsProjectId && this.salesOrder.logisticsProjectName) {
			return this.salesOrder.logisticsProjectName;
		}
		return this.projectNames[projectId] || 'Linked project';
	}

	private loadProjectNames(order: SalesOrder): void {
		const projectIds = new Set<number>([
			order.logisticsProjectId,
			...(order.salesOrderLines || []).map(line => line.logisticsProjectId)
		].filter((id): id is number => !!id));
		const companyId = order.owningCompanyId || this.sessionStorageService.getCompany()?.id;
		if (!companyId || !projectIds.size) {
			this.projectNames = {};
			return;
		}

		this.logisticsProjectsService.lookupProjects(companyId, '', Array.from(projectIds)).pipe(
			catchError(() => of([] as LogisticsProjectSummary[]))
		).subscribe(projects => {
			this.projectNames = projects.reduce<Record<number, string>>((names, project) => {
				if (project.projectId && project.projectName) {
					names[project.projectId] = project.projectName;
				}
				return names;
			}, {});
		});
	}

	visibleHeaderLifecycleActions(): HeroLifecycleAction[] {
		return this.heroLifecycleActions.filter(action => this.canOpenAction(action.code));
	}

	showEditAction(): boolean {
		return this.permissionService.hasPermission(app_permissions.order.records.edit);
	}

	showCreateAction(): boolean {
		return this.permissionService.hasPermission(app_permissions.order.records.create);
	}

	downloadPdf() {
		if (!this.salesOrder) {
			return;
		}
		const doc = new jsPDF({ unit: 'pt', format: 'a4' });
		const margin = 40;
		const pageHeight = doc.internal.pageSize.getHeight();
		const currency = this.currencyCode || this.salesOrder.currencyCode || 'SAR';
		let y = margin;
		const writeLine = (label: string, value?: string | number | null, spacing = 18) => {
			if (y > pageHeight - margin) {
				doc.addPage();
				y = margin;
			}
			doc.setFont('helvetica', 'bold');
			doc.text(label, margin, y);
			doc.setFont('helvetica', 'normal');
			doc.text(String(value ?? '-'), margin + 150, y);
			y += spacing;
		};

		doc.setFont('helvetica', 'bold');
		doc.setFontSize(18);
		doc.text('Sales Order', margin, y);
		y += 26;
		doc.setFontSize(10);
		doc.setFont('helvetica', 'normal');
		doc.text(`Generated ${new Date().toLocaleString()}`, margin, y);
		y += 24;

		writeLine('Order Number', this.salesOrder.orderNumber);
		writeLine(this.crmActive() ? 'CRM Client' : 'Customer', this.getPartyName());
		writeLine('CRM Contact', this.selectedCrmContactLabel());
		writeLine('Order Date', this.salesOrder.orderDate ? new Date(this.salesOrder.orderDate).toLocaleDateString() : '-');
		writeLine('Expected Date', this.salesOrder.expectedDate ? new Date(this.salesOrder.expectedDate).toLocaleDateString() : '-');
		writeLine('Payment Term', this.salesOrder.paymentTerm?.name || '-');
		writeLine('Salesperson', this.salesOrder.salesperson?.name || '-');
		writeLine('Status', this.getStatusLabel(this.salesOrder.state));
		writeLine('Approval', this.getStatusLabel(this.getApprovalStatus()));
		writeLine('Fulfillment', this.getStatusLabel(this.getFulfillmentStatus()));
		writeLine('Billing', this.getStatusLabel(this.getBillingStatus()));
		writeLine('Total', `${currency} ${this.getTotalAmount().toFixed(2)}`);

		y += 12;
		doc.setFont('helvetica', 'bold');
		doc.text('Lines', margin, y);
		y += 20;
		this.getLines().forEach((line, index) => {
			if (y > pageHeight - 80) {
				doc.addPage();
				y = margin;
			}
			doc.setFont('helvetica', 'bold');
			doc.text(`${index + 1}. ${line.product?.name || line.description || 'Order line'}`, margin, y);
			y += 14;
			doc.setFont('helvetica', 'normal');
			doc.text(
				`Qty ${Number(line.quantity || 0).toFixed(2)} | Unit ${currency} ${Number(line.unitPrice || 0).toFixed(4)} | Total ${currency} ${this.getLineTotal(line).toFixed(2)}`,
				margin + 14,
				y
			);
			y += 18;
			if (line.description) {
				const descriptionLines = doc.splitTextToSize(line.description, 500);
				doc.text(descriptionLines, margin + 14, y);
				y += (descriptionLines.length * 12) + 8;
			}
		});

		doc.save(`sales-order-${(this.salesOrder.orderNumber || this.salesOrder.orderID || 'draft').toString().replace(/[^a-zA-Z0-9._-]+/g, '_')}.pdf`);
	}

	getTotalAmount(): number {
		if (this.salesOrder?.totalAmount !== undefined && this.salesOrder.totalAmount !== null) {
			return Number(this.salesOrder.totalAmount);
		}
		if (!this.salesOrder || !this.salesOrder.salesOrderLines) {
			return 0;
		}
		return this.salesOrder.salesOrderLines.reduce((total, line) => total + line.totalLineAmount, 0);
	}

	getSubTotal(): number {
		if (this.salesOrder?.subtotalAmount !== undefined && this.salesOrder.subtotalAmount !== null) {
			return Number(this.salesOrder.subtotalAmount);
		}
		if (!this.salesOrder || !this.salesOrder.salesOrderLines) {
			return 0;
		}
		return this.salesOrder.salesOrderLines.reduce((total, line) => total + this.getLineBaseAmount(line), 0);
	}

	getTotalTax(): number {
		if (this.salesOrder?.taxAmount !== undefined && this.salesOrder.taxAmount !== null) {
			return Number(this.salesOrder.taxAmount);
		}
		if (!this.salesOrder || !this.salesOrder.salesOrderLines) {
			return 0;
		}
		return this.salesOrder.salesOrderLines.reduce((total, line) => total + line.totalTax, 0);
	}

	getStatusSeverity(arg0: string | undefined): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
		return getOrderStatusMetadata(arg0).severity;
	}

	canRunAction(action: string): boolean {
		return this.actionPermissionAllowed(action) && !!this.actionMetadata?.allowedActions?.includes(action);
	}

	canOpenAction(action: string): boolean {
		return this.canRunAction(action) || this.canOverrideCreditAction(action);
	}

	openAction(action: string): void {
		if (this.actionLoading || !this.canOpenAction(action)) {
			return;
		}
		this.pendingAction = action;
		this.actionReason = '';
		this.actionReasonCode = '';
		this.creditOverrideId = undefined;
		if (this.actionRequiresDialog(action)) {
			this.actionDialogVisible = true;
			return;
		}
		this.runAction();
	}

	runAction(): void {
		if (this.actionLoading || !this.salesOrder?.orderID || !this.pendingAction) {
			return;
		}
		if (this.actionRequiresReason(this.pendingAction) && !this.actionReason.trim()) {
			this.messageService.add({ severity: 'warn', summary: 'Reason required', detail: 'Enter a reason before continuing.' });
			return;
		}
		if (this.actionRequiresCreditOverride(this.pendingAction) && !this.creditOverrideId) {
			this.messageService.add({ severity: 'warn', summary: 'Override required', detail: 'Enter an approved credit override ID before continuing.' });
			return;
		}
		const request: SalesOrderLifecycleActionRequest = {
			actorId: this.userId(),
			reason: this.actionReason || undefined,
			reasonCode: this.actionReasonCode || undefined,
			comment: this.actionReason || undefined,
			creditOverrideId: this.creditOverrideId
		};
		this.actionLoading = true;
		this.ordersService.runSalesOrderAction(this.salesOrder.orderID, this.actionPath(this.pendingAction), request).subscribe({
			next: () => {
				this.messageService.add({ severity: 'success', summary: 'Updated', detail: `${this.actionLabel(this.pendingAction)} completed.` });
				this.actionDialogVisible = false;
				this.actionLoading = false;
				this.load();
			},
			error: (error) => {
				this.messageService.add({ severity: 'error', summary: 'Action failed', detail: error?.error?.message || 'Unable to run sales order action.' });
				this.actionLoading = false;
			}
		});
	}

	actionRequiresReason(action: string): boolean {
		return this.actionMetadata?.requiresReason?.includes(action)
			|| ['REJECT', 'HOLD', 'RELEASE_HOLD', 'CANCEL', 'REOPEN'].includes(action);
	}

	actionRequiresDialog(action: string): boolean {
		return this.actionRequiresReason(action) || this.actionRequiresCreditOverride(action);
	}

	actionRequiresCreditOverride(action: string): boolean {
		return this.canOverrideCreditAction(action);
	}

	canOverrideCreditAction(action: string): boolean {
		if (!this.actionPermissionAllowed(action) || !['CONFIRM', 'RELEASE_HOLD'].includes(action)) {
			return false;
		}
		const reason = this.actionMetadata?.blockedReasons?.[action] || '';
		const snapshot = this.actionMetadata?.creditSnapshot;
		return snapshot?.creditStatus === 'BLOCKED'
			&& !!reason
			&& /credit|overdue/i.test(reason);
	}

	actionLabel(action: string): string {
		return action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
	}

	actionPath(action: string): string {
		return action.toLowerCase().replace(/_/g, '-');
	}

	getStatusLabel(status: string | undefined): string {
		return getOrderStatusMetadata(status).label;
	}

	getStatusTooltip(status: string | undefined): string {
		return getOrderStatusMetadata(status).tooltip;
	}

	heroSubtitle(): string {
		return [
			this.salesOrder?.customer?.name || 'N/A',
			this.salesOrder?.orderDate ? `Order ${new Date(this.salesOrder.orderDate).toLocaleDateString()}` : null,
			this.salesOrder?.expectedDate ? `Expected ${new Date(this.salesOrder.expectedDate).toLocaleDateString()}` : null,
			this.formatMoney(this.getTotalAmount())
		].filter(Boolean).join(' | ');
	}

	formatMoney(amount: number | undefined | null): string {
		return amount !== undefined && amount !== null ? `${this.currencyCode} ${Number(amount).toFixed(currencyFractionDigits(this.currencyCode))}` : '-';
	}

	detailMetrics(): PageMetric[] {
		return [
			{ label: 'Order Total', value: this.formatMoney(this.getTotalAmount()), hint: `${this.currencyCode} base amount`, tone: 'success' },
			{ label: 'Lines', value: this.getLineCount(), hint: 'Commercial lines', tone: 'info' },
			{ label: 'Tax', value: this.formatMoney(this.getTotalTax()), hint: this.getLineCount() ? 'Calculated by line' : 'No lines' },
			{ label: 'Expected', value: this.salesOrder?.expectedDate ? new Date(this.salesOrder.expectedDate).toLocaleDateString() : '-', hint: this.salesOrder?.paymentTerm?.name || 'No payment term', tone: 'warn' }
		];
	}

	getLineTaxLabel(line: SalesOrderLine): string {
		const rate = line.taxRate !== undefined && line.taxRate !== null ? ` ${Number(line.taxRate).toFixed(2)}%` : '';
		return `${line.taxCode || 'Tax'}${rate}`;
	}

	onTabChange(value: string | number): void {
		this.activeTabIndex = Number(value);
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { tab: this.tabKeys[this.activeTabIndex] || this.tabKeys[0] },
			queryParamsHandling: 'merge',
			replaceUrl: true
		});
	}

	getLines(): SalesOrderLine[] {
		return this.salesOrder?.salesOrderLines ?? [];
	}

	getLineBaseAmount(line: SalesOrderLine): number {
		return line.totalLineAmount != null && line.totalTax != null
			? Number(line.totalLineAmount) - Number(line.totalTax) + Number(line.appliedLineDiscount || 0)
			: Number(line.quantity || 0) * Number(line.unitPrice || 0);
	}

	getLineTotal(line: SalesOrderLine): number {
		return Number(line.totalLineAmount ?? (this.getLineBaseAmount(line) + Number(line.totalTax || 0)));
	}

	getDiscountAmount(): number {
		if (this.salesOrder?.discountAmount !== undefined && this.salesOrder.discountAmount !== null) {
			return Number(this.salesOrder.discountAmount);
		}
		return this.getLines().reduce((total, line) => total + Number(line.appliedLineDiscount || 0), 0);
	}

	getLineCount(): number {
		return this.getLines().length;
	}

	getApprovalStatus(): string {
		return this.salesOrder?.approvalStatus || 'NOT_REQUIRED';
	}

	getFulfillmentStatus(): string {
		if (this.isServiceOnlyOrder()) {
			return 'NOT_REQUIRED';
		}
		return this.salesOrder?.fulfillmentStatus || 'NOT_STARTED';
	}

	getBillingStatus(): string {
		return this.salesOrder?.billingStatus || 'NOT_INVOICED';
	}

	getHoldStatus(): string {
		return this.salesOrder?.holdStatus || 'CLEAR';
	}

	getCreditStatus(): string {
		return this.salesOrder?.creditStatus || this.actionMetadata?.creditSnapshot?.creditStatus || 'CLEAR';
	}

	getCreditLimit(): number | undefined {
		return this.salesOrder?.creditLimit ?? this.actionMetadata?.creditSnapshot?.creditLimit;
	}

	getAvailableCredit(): number | undefined {
		return this.salesOrder?.availableCredit ?? this.actionMetadata?.creditSnapshot?.availableCredit;
	}

	getCreditCheckModeLabel(): string {
		switch ((this.salesOrder?.creditCheckMode || this.actionMetadata?.creditSnapshot?.creditCheckMode || 'ENFORCE').toUpperCase()) {
			case 'WARN': return 'Warn Only';
			case 'DISABLED': return 'Disabled';
			default: return 'Enforce and Block';
		}
	}

	getPartyName(): string {
		if (this.crmActive()) {
			return this.salesOrder?.crmClient?.companyName || this.salesOrder?.customer?.name || '-';
		}
		return this.salesOrder?.customer?.name || '-';
	}

	getCompanyName(): string {
		return this.salesOrder?.company?.name || '-';
	}

	getCompanyOrCrmLabel(): string {
		return this.crmActive() ? 'CRM Client' : 'Company';
	}

	getCompanyOrCrmValue(): string {
		return this.crmActive()
			? (this.salesOrder?.crmClient?.companyName || '-')
			: this.getCompanyName();
	}

	getCrmContactLabels(): string[] {
		return (this.salesOrder?.crmClientContacts || []).map(contact =>
			[
				contact.name,
				contact.communicationType,
				contact.email,
				contact.phone || contact.mobile
			].filter(Boolean).join(' | ')
		).filter(Boolean);
	}

	crmActive(): boolean {
		return this.enterpriseFeatureService.getIsCrmSubscribed();
	}

	selectedCrmContact() {
		const selectedId = this.salesOrder?.crmClientContactId || this.salesOrder?.crmClientContact?.id;
		if (!selectedId) {
			return this.salesOrder?.crmClientContact;
		}
		return this.salesOrder?.crmClientContact
			|| (this.salesOrder?.crmClientContacts || []).find(contact => contact.id === selectedId);
	}

	selectedCrmContactLabel(): string {
		const contact = this.selectedCrmContact();
		if (!contact) {
			return (this.salesOrder?.crmClientContacts || []).length ? 'No CRM contact selected' : '-';
		}
		return [
			contact.name,
			contact.communicationType,
			contact.email,
			contact.phone || contact.mobile
		].filter(Boolean).join(' | ');
	}

	hasSourceQuotation(): boolean {
		return !!this.salesOrder?.sourceQuotationId;
	}

	viewSourceQuotation(): void {
		if (this.salesOrder?.sourceQuotationId) {
			this.router.navigate(['/crm/quotations', this.salesOrder.sourceQuotationId]);
		}
	}

	getBlockedActions(): { action: string; reason: string }[] {
		const blocked = this.actionMetadata?.blockedReasons || {};
		return Object.keys(blocked)
			.filter(action => !this.shouldHideBlockedAction(action))
			.map(action => ({ action, reason: blocked[action] }));
	}

	blockedReasonFor(action: string): string {
		return this.actionMetadata?.blockedReasons?.[action] || '';
	}

	getCreditWarnings(): string[] {
		return this.actionMetadata?.creditSnapshot?.warnings ?? [];
	}

	getCreditBlockers(): string[] {
		return this.actionMetadata?.creditSnapshot?.blockers ?? [];
	}

	actionPermissionAllowed(action: string): boolean {
		if (action === 'APPROVE' || action === 'REJECT' || action === 'CONFIRM') {
			return this.permissionService.hasPermission(app_permissions.order.records.approve);
		}
		if (action === 'CANCEL') {
			return this.permissionService.hasPermission(app_permissions.order.records.cancel);
		}
		return this.permissionService.hasPermission(app_permissions.order.records.edit);
	}

	startNotesEdit(): void {
		this.notesDraft = this.salesOrder?.notes || '';
		this.notesEditing = true;
	}

	cancelNotesEdit(): void {
		this.notesDraft = this.salesOrder?.notes || '';
		this.notesEditing = false;
	}

	saveNotes(): void {
		if (!this.salesOrder?.orderID) {
			return;
		}
		this.notesSaving = true;
		this.ordersService.updateSalesOrderNotes(this.salesOrder.orderID, this.notesDraft || '', this.userId()).subscribe({
			next: order => {
				this.salesOrder = order;
				this.notesDraft = order.notes || '';
				this.notesEditing = false;
				this.notesSaving = false;
				this.messageService.add({ severity: 'success', summary: 'Notes saved', detail: 'Internal notes were updated.' });
			},
			error: error => {
				this.notesSaving = false;
				this.messageService.add({ severity: 'error', summary: 'Notes failed', detail: error?.error?.message || error?.error?.error || 'Unable to update internal notes.' });
			}
		});
	}

	onSalesOrderFileUpload(event: any): void {
		const file: File | undefined = event.files?.[0];
		if (!file || !this.salesOrder?.orderID) {
			return;
		}
		const orderId = this.salesOrder.orderID;
		const owningCompanyId = this.salesOrder.owningCompanyId || this.sessionStorageService.getCompany()?.id;
		if (!owningCompanyId) {
			this.messageService.add({ severity: 'warn', summary: 'Company missing', detail: 'Cannot link the file without an owning company.' });
			return;
		}
		this.attachmentUploading = true;
		this.attachmentUploadProgress = 0;
		this.firebaseStorageService.uploadFileWithProgress(file, `orders/sales-orders/${orderId}/attachments`).subscribe({
			next: upload => {
				this.attachmentUploadProgress = upload.progress;
				if (!upload.url) {
					return;
				}
				const attachment: SalesOrderAttachment = {
					attachmentId: 0,
					orderId,
					owningCompanyId,
					fileName: file.name,
					filePath: upload.url,
					fileType: file.type || 'Attachment',
					fileSize: file.size,
					uploadedAt: new Date(),
					uploadedBy: this.userId(),
					fileContent: ''
				};
				this.ordersService.createSalesOrderAttachment(orderId, owningCompanyId, attachment).subscribe({
					next: saved => {
						if (this.salesOrder) {
							this.salesOrder.attachments = [...(this.salesOrder.attachments || []), saved];
						}
						this.attachmentUploading = false;
						this.attachmentUploadProgress = 0;
						this.messageService.add({ severity: 'success', summary: 'Uploaded', detail: `${file.name} uploaded.` });
					},
					error: error => {
						this.attachmentUploading = false;
						this.attachmentUploadProgress = 0;
						this.messageService.add({
							severity: 'error',
							summary: 'Link failed',
							detail: error?.error?.message || error?.error?.error
								|| (typeof error?.error === 'string' ? error.error : null)
								|| error?.message
								|| 'File uploaded to Firebase, but could not be linked to the sales order.'
						});
					}
				});
			},
			error: error => {
				this.attachmentUploading = false;
				this.attachmentUploadProgress = 0;
				this.messageService.add({ severity: 'error', summary: 'Upload failed', detail: error?.message || 'Unable to upload file.' });
			}
		});
	}

	downloadAttachment(file: SalesOrderAttachment): void {
		if (file.filePath) {
			window.open(file.filePath, '_blank');
		}
	}

	openAttachmentDelete(file: SalesOrderAttachment): void {
		if (!this.showEditAction() || !file.attachmentId) return;
		this.attachmentToDelete = file;
		this.attachmentDeleteReason = '';
		this.attachmentDeleteError = '';
		this.attachmentDeleteDialogVisible = true;
	}

	deleteAttachment(): void {
		const file = this.attachmentToDelete;
		if (!file?.attachmentId || !this.showEditAction() || !this.attachmentDeleteReason.trim() || this.attachmentDeleting) return;
		this.attachmentDeleting = true;
		this.attachmentDeleteError = '';
		this.ordersService.deleteSalesOrderAttachment(file.attachmentId, this.attachmentDeleteReason.trim()).subscribe({
			next: () => {
				if (this.salesOrder) {
					this.salesOrder.attachments = this.salesOrder.attachments?.filter(item => item.attachmentId !== file.attachmentId) ?? [];
				}
				this.attachmentToDelete = undefined;
				this.attachmentDeleteDialogVisible = false;
				this.attachmentDeleting = false;
				this.messageService.add({ severity: 'success', summary: 'Attachment removed', detail: `${file.fileName} was removed from this order.` });
				this.load();
			},
			error: error => {
				this.attachmentDeleting = false;
				this.attachmentDeleteError = error?.error?.message || 'Could not remove this attachment.';
				this.messageService.add({ severity: 'error', summary: 'Removal failed', detail: this.attachmentDeleteError });
			}
		});
	}

	formatFileSize(size: number | undefined | null): string {
		if (!size) {
			return '0 MB';
		}
		return `${(Number(size) / 1024 / 1024).toFixed(2)} MB`;
	}

	private userId(): number {
		return this.sessionStorageService.getCustomer()?.id ?? this.sessionStorageService.getUser()?.id ?? 0;
	}

	private resolveTabIndex(tab: string | null): number {
		const index = this.tabKeys.findIndex(value => value === (tab || '').toLowerCase());
		return index >= 0 ? index : 0;
	}

	private normalizeServiceOrderActiveTab(): void {
		if (this.isServiceOnlyOrder() && this.activeTabIndex === 6) {
			this.activeTabIndex = 7;
		}
	}

	private shouldHideBlockedAction(action: string): boolean {
		if (!this.isServiceOnlyOrder()) {
			return false;
		}
		const normalized = (action || '').trim().toUpperCase().replaceAll(' ', '_');
		return normalized === 'SYNC_FULFILLMENT'
			|| normalized === 'CREATE_DELIVERY_RELEASE'
			|| normalized === 'CREATE_RETURN_RELEASE';
	}

	private productTypeFor(line: SalesOrderLine | undefined): string {
		return `${line?.product?.productType || ''}`.trim().toUpperCase();
	}

	isServiceLine(line: SalesOrderLine | undefined): boolean {
		return this.productTypeFor(line) === ProductType.SERVICE;
	}

	isServiceOnlyOrder(): boolean {
		const lines = this.getLines();
		return lines.length > 0 && lines.every(line => this.isServiceLine(line));
	}

	// ---- Phase 5: Fulfillment & Invoice Readiness Methods ----

	canCreateDeliveryRelease(): boolean {
		if (this.isServiceOnlyOrder()) {
			return false;
		}
		const state = (this.salesOrder?.state || '').toUpperCase();
		return ('CONFIRMED' === state || 'APPROVED' === state)
			&& (this.salesOrder?.holdStatus || '').toUpperCase() !== 'ON_HOLD'
			&& this.getLines().length > 0;
	}

	canCreateReturnRelease(): boolean {
		if (this.isServiceOnlyOrder()) {
			return false;
		}
		return this.canCreateDeliveryRelease()
			&& this.getLines().some(l => (l.deliveredQuantity || 0) > (l.returnedQuantity || 0));
	}

	canPostToAccounts(): boolean {
		return this.invoiceReadiness?.canStage
			?? ((this.invoiceReadiness?.totalReadyToInvoiceAmount || 0) > 0
				&& !this.invoiceReadiness?.hasBlockingReasons);
	}

	openDeliveryReleaseDialog(): void {
		this.deliveryReleaseDialogVisible = true;
		this.deliveryReleaseRequestId = this.newRequestId();
		this.deliveryDestinationLocationId = this.salesOrder?.shipToLocationId;
		this.deliveryWindowStart = this.salesOrder?.deliveryWindowStart
			? new Date(this.salesOrder.deliveryWindowStart) : undefined;
		this.deliveryWindowEnd = this.salesOrder?.deliveryWindowEnd
			? new Date(this.salesOrder.deliveryWindowEnd) : undefined;
		// Load readiness
		const id = this.salesOrder?.orderID;
		if (!id) return;
		this.ordersService.getFulfillmentReadiness(id).subscribe(data => {
			this.fulfillmentReadiness = data;
			this.deliveryDestinationLocationId ??= data.shipToLocationId;
			this.deliveryWindowStart ??= data.deliveryWindowStart ? new Date(data.deliveryWindowStart) : undefined;
			this.deliveryWindowEnd ??= data.deliveryWindowEnd ? new Date(data.deliveryWindowEnd) : undefined;
			this.deliveryReleaseLines = (data.lines || []).filter(line => line.eligible).map(line => {
				const source = this.getLines().find(candidate => candidate.lineID === line.lineId);
				const factor = source?.conversionFactorToBase || 1;
				const maximum = Math.max(0, Math.min(line.remainingQuantity || 0,
					(line.releaseableBaseQuantity || 0) / factor));
				return { lineId: line.lineId, lineNumber: line.lineNumber, productName: line.productName,
					quantity: maximum, maximum, warehouseId: line.sourceWarehouseId };
			});
		});
	}

	openReturnReleaseDialog(): void {
		this.returnReleaseDialogVisible = true;
		const id = this.salesOrder?.orderID;
		if (!id) return;
		this.ordersService.getFulfillmentReadiness(id).subscribe(data => {
			this.fulfillmentReadiness = data;
		});
	}

	syncFulfillment(): void {
		if (this.isServiceOnlyOrder()) {
			return;
		}
		const id = this.salesOrder?.orderID;
		if (!id) return;
		this.syncLoading = true;
		this.ordersService.syncFulfillment(id).subscribe({
			next: () => {
				this.messageService.add({ severity: 'success', summary: 'Synced', detail: 'Fulfillment quantities synchronized.' });
				this.syncLoading = false;
				this.load();
			},
			error: (err) => {
				this.messageService.add({ severity: 'error', summary: 'Sync failed', detail: err?.error?.message || 'Unable to sync fulfillment.' });
				this.syncLoading = false;
			}
		});
	}

	allocationForLine(lineId: number): any {
		return this.fulfillmentReadiness?.lines?.find(line => line.lineId === lineId);
	}

	retryAllocation(): void {
		const id = this.salesOrder?.orderID;
		if (!id) return;
		this.actionLoading = true;
		const lineIds = this.getLines().filter(line => !!line.inventoryItemId).map(line => line.lineID);
		this.ordersService.retrySalesOrderAllocation(id, lineIds, this.userId()).subscribe({
			next: readiness => {
				this.fulfillmentReadiness = readiness;
				this.actionLoading = false;
				this.messageService.add({ severity: 'success', summary: 'Allocation refreshed', detail: 'FEFO allocation was retried against released, unexpired lots.' });
				this.load();
			},
			error: err => {
				this.actionLoading = false;
				this.messageService.add({ severity: 'error', summary: 'Allocation failed', detail: err?.error?.message || 'Unable to retry allocation.' });
			}
		});
	}

	createDeliveryRelease(): void {
		const id = this.salesOrder?.orderID;
		if (!id) return;
		const lines: ReleaseLineRequest[] = this.deliveryReleaseLines
			.filter(line => line.quantity > 0)
			.map(line => ({ salesOrderLineId: line.lineId, quantity: line.quantity }));
		if (!lines.length) {
			this.messageService.add({ severity: 'warn', summary: 'No lines', detail: 'No eligible lines to release.' });
			return;
		}
		const request: CreateReleaseRequest = {
			requestId: this.deliveryReleaseRequestId || this.newRequestId(),
			lines,
			owningCompanyId: this.salesOrder?.owningCompanyId,
			actorId: this.userId(),
			destinationLocationId: this.deliveryDestinationLocationId,
			deliveryWindowStart: this.deliveryWindowStart,
			deliveryWindowEnd: this.deliveryWindowEnd
		};
		this.actionLoading = true;
		this.ordersService.createTransportRelease(id, request).subscribe({
			next: (result) => {
				this.fulfillmentReleases = result;
				this.deliveryReleaseDialogVisible = false;
				this.actionLoading = false;
				this.messageService.add({ severity: 'success', summary: 'Created', detail: 'Delivery transport release created.' });
				this.load();
			},
			error: (err) => {
				this.actionLoading = false;
				this.messageService.add({ severity: 'error', summary: 'Failed', detail: err?.error?.message || 'Unable to create transport release.' });
			}
		});
	}

	createReturnRelease(): void {
		const id = this.salesOrder?.orderID;
		if (!id) return;
		const lines: ReleaseLineRequest[] = this.getLines()
			.filter(l => (l.deliveredQuantity || 0) > (l.returnedQuantity || 0))
			.map(l => ({
				salesOrderLineId: l.lineID,
				salesOrderLineNumber: l.lineNumber,
				productId: l.product?.productID,
				variantId: l.variant?.variantID,
				itemCode: l.product?.productCode,
				itemName: l.product?.name,
				itemDescription: l.description,
				quantity: (l.deliveredQuantity || 0) - (l.returnedQuantity || 0),
				uom: 'EA'
			}));
		if (!lines.length) {
			this.messageService.add({ severity: 'warn', summary: 'No lines', detail: 'No delivered, unreturned lines.' });
			return;
		}
		const outboundReleaseId = this.getLines().find(l => l.outboundReleaseId)?.outboundReleaseId;
		const request: CreateReturnReleaseRequest = { lines, owningCompanyId: this.salesOrder?.owningCompanyId, outboundReleaseId };
		this.actionLoading = true;
		this.ordersService.createReturnRelease(id, request).subscribe({
			next: (result) => {
				this.fulfillmentReleases = result;
				this.returnReleaseDialogVisible = false;
				this.actionLoading = false;
				this.messageService.add({ severity: 'success', summary: 'Created', detail: 'Return transport release created.' });
				this.load();
			},
			error: (err) => {
				this.actionLoading = false;
				this.messageService.add({ severity: 'error', summary: 'Failed', detail: err?.error?.message || 'Unable to create return release.' });
			}
		});
	}

	postToAccounts(): void {
		const id = this.salesOrder?.orderID;
		if (!id || !this.canPostToAccounts() || this.draftLoading) return;
		this.draftLoading = true;
		this.ordersService.stageSalesOrderForConsolidation(id, {
			owningCompanyId: this.salesOrder?.owningCompanyId,
			actorId: this.userId()
		}).subscribe({
			next: result => {
				this.draftLoading = false;
				this.messageService.add({
					severity: result.warnings?.length ? 'warn' : 'success',
					summary: result.stagingOutcome === 'UPDATED' ? 'Receivable updated' :
						result.stagingOutcome?.startsWith('SKIPPED') ? 'Receivable unchanged' : 'Receivable staged',
					detail: result.warnings?.join(' ') || (result.stagingOutcome?.startsWith('SKIPPED')
						? 'The existing receivable was left unchanged for finance review.' : 'Ready in To Invoice.')
				});
				if (result.receivableId && !result.stagingOutcome?.startsWith('SKIPPED')) {
					this.router.navigate(['/accounts/enterprise/receivables/to-be-invoiced/view', result.receivableId]);
				} else {
					this.loadFulfillmentData();
				}
			},
			error: err => {
				this.draftLoading = false;
				this.messageService.add({ severity: 'error', summary: 'Staging failed', detail: err?.error?.message || 'Unable to stage receivable for consolidation.' });
			}
		});
	}

	openLineSidePanel(line: SalesOrderLine): void {
		this.selectedLine = line;
		this.lineSidePanelVisible = true;
	}

	getReleaseTypeLabel(releaseRole: string): string {
		switch (releaseRole) {
			case 'OUTBOUND': return 'Delivery';
			case 'RETURN': return 'Return';
			default: return releaseRole || 'Unknown';
		}
	}

	getReleaseDirectionLabel(direction?: string): string {
		return direction === 'RETURN' ? 'Return' : 'Outbound';
	}

	isCancellableRelease(release: any): boolean {
		return !release.parentReleaseId && release.fulfillmentDirection === 'OUTBOUND'
			&& !release.plannedShipmentId && !['CANCELLED', 'COMPLETED', 'CLOSED', 'RELEASED_TO_SHIPMENT']
				.includes((release.releaseStatus || '').toUpperCase());
	}

	canCorrectQuantity(line: SalesOrderLine): boolean {
		return !!line.lineID
			&& this.permissionService.hasPermission(app_permissions.order.records.edit)
			&& this.permissionService.hasPermission(app_permissions.transport_planning.plans.apply);
	}

	openQuantityCorrection(line: SalesOrderLine): void {
		const orderId = this.salesOrder?.orderID;
		if (!orderId || !line.lineID || !this.canCorrectQuantity(line)) return;
		this.correctionLoading = true;
		this.ordersService.previewSalesOrderQuantityCorrection(orderId, line.lineID).subscribe({
			next: preview => {
				this.correctionPreview = preview;
				this.correctionQuantity = preview.quantity;
				this.correctionReason = '';
				this.correctionGroups = preview.groups.map(group => ({ rootReleaseId: group.rootReleaseId,
					originName: group.originName, destinationName: group.destinationName,
					replacement: { ...group.replacement, lines: group.replacement.lines.map(item => ({ ...item })),
						pickupWindowStart: this.correctionDate(group.replacement.pickupWindowStart),
						pickupWindowEnd: this.correctionDate(group.replacement.pickupWindowEnd),
						deliveryWindowStart: this.correctionDate(group.replacement.deliveryWindowStart),
						deliveryWindowEnd: this.correctionDate(group.replacement.deliveryWindowEnd) } }));
				this.correctionVisible = true;
				this.correctionLoading = false;
			},
			error: err => {
				this.correctionLoading = false;
				this.messageService.add({ severity: 'error', summary: 'Correction unavailable',
					detail: err?.error?.message || 'Unable to preview linked releases.' });
			}
		});
	}

	private correctionDate(value?: Date | string): Date | undefined {
		return value ? new Date(value) : undefined;
	}

	onCorrectionQuantityChange(quantity?: number): void {
		this.correctionQuantity = quantity;
		let remaining = Math.max(0, quantity || 0);
		const lineId = this.correctionPreview?.lineId;
		for (const group of this.correctionGroups) {
			for (const line of group.replacement.lines) {
				if (line.salesOrderLineId !== lineId) continue;
				const original = this.correctionPreview?.groups.find(item => item.rootReleaseId === group.rootReleaseId)
					?.replacement.lines.find(item => item.salesOrderLineId === lineId)?.quantity || 0;
				line.quantity = Math.min(original, remaining);
				remaining -= line.quantity;
			}
		}
	}

	submitQuantityCorrection(): void {
		const preview = this.correctionPreview;
		const orderId = this.salesOrder?.orderID;
		if (!preview || !orderId || this.correctionLoading) return;
		if (!this.correctionQuantity || this.correctionQuantity <= 0 || this.correctionQuantity === preview.quantity) {
			this.messageService.add({ severity: 'warn', summary: 'Quantity', detail: 'Enter a new positive quantity.' });
			return;
		}
		if (!this.correctionReason.trim() || this.correctionReason.trim().length > 500) {
			this.messageService.add({ severity: 'warn', summary: 'Reason', detail: 'Enter a reason of up to 500 characters.' });
			return;
		}
		this.correctionLoading = true;
		this.ordersService.applySalesOrderQuantityCorrection(orderId, preview.lineId, {
			expectedVersion: preview.versionNumber, quantity: this.correctionQuantity,
			reason: this.correctionReason.trim(), groups: this.correctionGroups
		}).subscribe({
			next: result => {
				this.correctionLoading = false;
				this.correctionVisible = false;
				this.messageService.add({ severity: 'success', summary: 'Quantity corrected',
					detail: `${result.cancelledReleaseIds.length} old releases cancelled; ${result.replacementReleaseIds.length} new releases created.` });
				this.load();
			},
			error: err => {
				this.correctionLoading = false;
				this.messageService.add({ severity: 'error', summary: 'Correction blocked',
					detail: err?.error?.message || 'No changes were saved.' });
			}
		});
	}

	openCancelRelease(rootReleaseId: number): void {
		this.cancelReleaseRootId = rootReleaseId;
		this.cancelReleaseReason = '';
		this.cancelReleaseVisible = true;
	}

	cancelDeliveryRelease(): void {
		const orderId = this.salesOrder?.orderID;
		const rootReleaseId = this.cancelReleaseRootId;
		if (!orderId || !rootReleaseId || !this.cancelReleaseReason.trim()) return;
		this.actionLoading = true;
		this.ordersService.cancelTransportRelease(orderId, rootReleaseId, this.cancelReleaseReason.trim()).subscribe({
			next: result => {
				this.fulfillmentReleases = result;
				this.actionLoading = false;
				this.cancelReleaseVisible = false;
				this.messageService.add({ severity: 'success', summary: 'Cancelled', detail: 'Release tree and warehouse work were cancelled.' });
				this.load();
			},
			error: err => {
				this.actionLoading = false;
				this.messageService.add({ severity: 'error', summary: 'Cancellation blocked', detail: err?.error?.message || 'Execution has already started.' });
			}
		});
	}

	private newRequestId(): string {
		return globalThis.crypto?.randomUUID?.() || `so-release-${Date.now()}-${Math.random().toString(16).slice(2)}`;
	}

	returnableLines(): SalesOrderLine[] {
		return this.getLines().filter(l => (l.deliveredQuantity || 0) > (l.returnedQuantity || 0));
	}

	hasReturnableLines(): boolean {
		return this.returnableLines().length > 0;
	}
}
